import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../../hooks/useAuth";
import { useLoading } from "../../contexts/LoadingContext";
import { v4 as uuidv4 } from "uuid";
import { useProjectApi } from "../../hooks/useProjectApi";
import { useProjectActivity } from "../../hooks/useProjectActivity";
import { useTimesheetApi } from "../../hooks/useTimesheetApi";
import { useHolidayApi } from "../../hooks/useHolidayApi";
import { useMilestoneActivityApi } from "../../hooks/useProjectMilestoneActivity";

const DUMMY_TIMESHEET_ENTRIES = Array(6)
  .fill({
    id: "",
    project_id: "",
    activity_id: "",
    milestone_activity_mapping_id: "",
    actuals_budget: "0/0",
    saturday: "0",
    sunday: "0",
    monday: "0",
    tuesday: "0",
    wednesday: "0",
    thursday: "0",
    friday: "0",
    total_hours: "",
    comments: "",
    project_manager_comments: [],
    is_draft: true,
  })
  .map((entry) => ({ ...entry, id: `temp-${uuidv4()}` }));

export const useTimesheetViewModel = () => {
  const [timesheetEntries, setTimesheetEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFriday, setSelectedFriday] = useState(null);
  const [fridays, setFridays] = useState([]);
  const [isDraft, setIsDraft] = useState(true);
  const [successModal, setSuccessModal] = useState(null);
  const [lastSavedDate, setLastSavedDate] = useState(null);
  const [holidays, setHolidays] = useState([]);

  const { user } = useAuth();
  const { showLoading, hideLoading } = useLoading();
  const { getProjectsByResourceId } = useProjectApi();
  const { getTasksByProjectResourceId } = useProjectActivity();
  const { getFridays, addWeeklyTimesheet, getResourceTimesheet, deleteTimesheet } =
    useTimesheetApi();
  const { getHolidays } = useHolidayApi();
  const { getMilestoneActivityByProjectAndTask } = useMilestoneActivityApi();

  const initialize = useCallback(async () => {
    if (loading) return;
    setLoading(true);

    try {
      const resourceId = parseInt(user.userDetails.resource_id);

      const [projectsResponse, fridaysResponse, holidaysResponse] = await Promise.all([
        getProjectsByResourceId(resourceId),
        getFridays(3, 2),
        getHolidays(),
      ]);

      if (!projectsResponse.success || !fridaysResponse.success) {
        throw new Error(projectsResponse.error || fridaysResponse.error);
      }

      if (holidaysResponse.success) {
        setHolidays(holidaysResponse.data);
      }

      const projectsWithStringId = projectsResponse.data.map((project) => ({
        ...project,
        project_id: project.project_id.toString(),
      }));

      setProjects(projectsWithStringId);
      setFridays(fridaysResponse.data);

      if (fridaysResponse.data.length > 0) {
        const initialFriday =
          fridaysResponse.data[2] || fridaysResponse.data[0];
        setSelectedFriday(initialFriday);
        await fetchTimesheetData(initialFriday, resourceId);
      }
    } catch (err) {
      console.error("Error initializing timesheet:", err);
      setError(err.message);
      setTimesheetEntries(DUMMY_TIMESHEET_ENTRIES);
    } finally {
      setLoading(false);
    }
  }, [user?.userDetails?.resource_id]);

  const fetchTimesheetData = useCallback(async (selectedWeek, resourceId) => {
    if (!selectedWeek?.weekNumber || !resourceId) return;

    try {
      const { success, data, error } = await getResourceTimesheet(
        selectedWeek.weekNumber,
        resourceId
      );

      if (!success) throw new Error(error || "Failed to fetch timesheet data");
      if (data.length > 0) {
        const hasAnyDraft = data.some((entry) => entry.is_draft);
        setIsDraft(hasAnyDraft);
        setLastSavedDate(data[0]?.updated_on || null);

        const transformTimesheetData = (entry) => ({
          ...entry,
          project_id: entry.project_id.toString(),
          project_manager_comments: (entry.project_manager_comments || []).map(
            (comment) => ({
              ...comment,
              created_on: comment.created_on,
            })
          ),
          resource_comments: (entry.resource_comments || []).map((comment) => ({
            ...comment,
            created_on: comment.created_on,
          })),
        });

        const transformedEntries = data?.length
          ? data.map((entry) => ({
            id: uuidv4(),
            project_id: entry.project_id.toString(),
            activity_id: entry.task_id.toString(),
            actuals_budget: `${entry?.resource_actual_hours ? entry?.resource_actual_hours : 0
              }/${entry.resource_budget_hours}`,
            comments: entry.resource_comment || "",
            ...transformTimesheetData(entry),
            total_hours: entry.total_hours_for_week || "0",
            resource_timesheets: entry.resource_timesheets,
            weekly_timesheet_id: entry.weekly_timesheet_id || null,
            milestone_activity_mapping_id: entry.milestone_activity_mapping_id ? entry.milestone_activity_mapping_id.toString() : "",
            is_draft: entry.is_draft,
            saturday: "0",
            sunday: "0",
            monday: "0",
            tuesday: "0",
            wednesday: "0",
            thursday: "0",
            friday: "0",
            ...entry.resource_timesheets.reduce((acc, day) => {
              try {
                const date = new Date(day.logged_date);
                if (isNaN(date.getTime())) return acc;

                const dayName = date
                  .toLocaleDateString("en-US", { weekday: "long" })
                  .toLowerCase();

                if (
                  [
                    "saturday",
                    "sunday",
                    "monday",
                    "tuesday",
                    "wednesday",
                    "thursday",
                    "friday",
                  ].includes(dayName)
                ) {
                  acc[dayName] = day.logged_hours;
                }
                return acc;
              } catch (err) {
                console.error("Error processing date:", err);
                return acc;
              }
            }, {}),
          }))
          : DUMMY_TIMESHEET_ENTRIES;

        setTimesheetEntries(transformedEntries);
      } else {
        setIsDraft(true);
        setLastSavedDate(null);
        setTimesheetEntries(DUMMY_TIMESHEET_ENTRIES);
      }
    } catch (err) {
      console.error("Error fetching timesheet data:", err);
      setError(err.message);
      setTimesheetEntries(DUMMY_TIMESHEET_ENTRIES);
      setIsDraft(true);
      setLastSavedDate(null);
    }
  }, []);

  const activitiesCache = useRef(new Map());
  const milestoneActivityCache = useRef(new Map());

  const fetchMilestoneActivityMappings = useCallback(
    async (projectId, taskId) => {
      if (!projectId || !taskId) return [];

      const cacheKey = `${projectId}-${taskId}`;
      if (milestoneActivityCache.current.has(cacheKey)) {
        return milestoneActivityCache.current.get(cacheKey);
      }

      try {
        const { success, data, error } = await getMilestoneActivityByProjectAndTask(
          parseInt(projectId),
          taskId
        );

        if (success && data && data.activities) {
          const mappings = data.activities.map((activity) => ({
            value: activity.milestone_activity_mapping_id.toString(),
            label: activity.activity_name,
            activity_id: activity.activity_id,
            created_on: activity.created_on,
            updated_on: activity.updated_on,
          }));

          milestoneActivityCache.current.set(cacheKey, mappings);
          return mappings;
        }
        return [];
      } catch (err) {
        console.error("Error fetching milestone activity mappings:", err);
        return [];
      }
    },
    [getMilestoneActivityByProjectAndTask]
  );

  const fetchActivitiesForProject = useCallback(
    async (projectId) => {
      if (!projectId) return [];

      if (activitiesCache.current.has(projectId)) {
        return activitiesCache.current.get(projectId);
      }

      try {
        const resourceId = parseInt(user.userDetails.resource_id);
        const { success, data, error } = await getTasksByProjectResourceId(
          projectId,
          resourceId
        );

        if (success && Array.isArray(data)) {
          const activities = data
            .filter((task) => task && task.task_id && task.task_name)
            .map((task) => ({
              value: task.task_id,
              label: task.task_name,
              resource_budget_hours:
                task.resource_mappings?.[0]?.resource_budget_hours || 0,
              resource_actual_hours:
                task.resource_mappings?.[0]?.resource_actual_hours || 0,
            }));

          activitiesCache.current.set(projectId, activities);
          return activities;
        }
        return [];
      } catch (err) {
        console.error("Error fetching activities:", err);
        return [];
      }
    },
    [user?.userDetails?.resource_id]
  );

  const handleFridayChange = useCallback(
    async (friday) => {
      if (!friday?.weekNumber || !user?.userDetails?.resource_id) return;

      setLoading(true);

      try {
        setSelectedFriday(friday);
        await fetchTimesheetData(friday, user.userDetails.resource_id);
      } catch (err) {
        console.error("Error changing week:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    },
    [user?.userDetails?.resource_id, fetchTimesheetData]
  );

  const handleDateChange = useCallback(
    async (date) => {
      if (!date || !user?.userDetails?.resource_id) return;

      setLoading(true);

      try {
        const selectedWeek = fridays.find(
          (friday) =>
            new Date(friday.value).toDateString() ===
            new Date(date).toDateString()
        );

        if (selectedWeek) {
          setSelectedFriday(selectedWeek);
          await fetchTimesheetData(selectedWeek, user.userDetails.resource_id);
        }
      } catch (err) {
        console.error("Error changing date:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    },
    [user?.userDetails?.resource_id, fridays, fetchTimesheetData]
  );

  useEffect(() => {
    initialize();
  }, [initialize]);

  const handleRowSave = async (updatedRow) => {
    try {
      setTimesheetEntries((prev) =>
        prev.map((row) => (row.id === updatedRow.id ? updatedRow : row))
      );
    } catch (err) {
      setError(err.message);
    }
  };

  const addNewRow = () => {
    const newRow = {
      id: `temp-${Date.now()}`,
      project_id: "",
      activity_id: "",
      milestone_activity_mapping_id: "",
      saturday: "0",
      sunday: "0",
      monday: "0",
      tuesday: "0",
      wednesday: "0",
      thursday: "0",
      friday: "0",
      total_hours: "0",
      comments: "",
      actuals_budget: "0/0",
      is_draft: true,
    };
    setTimesheetEntries([...timesheetEntries, newRow]);
  };

  const handleDelete = useCallback(async (timesheet) => {
    try {
      if (timesheet.id.startsWith("temp-")) {
        setTimesheetEntries((prev) => prev.filter((row) => row.id !== timesheet.id));
        return;
      }
      const result = await deleteTimesheet(timesheet.weekly_timesheet_id);
      if (result.success) {
        setSuccessModal({
          type: "success",
          title: "Success",
          message: "Timesheet entry deleted successfully",
        });
        setTimesheetEntries((prev) => prev.filter((row) => row.weekly_timesheet_id !== timesheet.weekly_timesheet_id));
      } else {
        throw new Error(result.error || "Failed to delete timesheet entry");
      }
      // setTimesheetEntries((prev) => prev.filter((row) => row.id !== id));
    } catch (err) {
      setError(err.message);
    }
  }, [deleteTimesheet]);

  const handleProjectChange = async (rowIndex, projectId) => {
    const updatedRows = [...timesheetEntries];
    const row = updatedRows[rowIndex];

    if (row.weekly_timesheet_id) {
      row.weekly_timesheet_id = null;
      row.timesheet_id = null;
    }
    updatedRows[rowIndex] = {
      ...row,
      project_id: projectId,
      activity_id: "",
      milestone_activity_mapping_id: "",
      actuals_budget: "0/0",
      saturday: "0",
      sunday: "0",
      monday: "0",
      tuesday: "0",
      wednesday: "0",
      thursday: "0",
      friday: "0",
    };

    setTimesheetEntries(updatedRows);
  };

  const WEEK_DAY_KEYS = [
    "saturday",
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
  ];

  const getWeekTotalHours = (row) =>
    WEEK_DAY_KEYS.reduce((sum, day) => sum + Number(row[day] || 0), 0);

  const hasRequiredId = (value) =>
    value != null && String(value).trim() !== "";

  const hasWeeklyTimesheetId = (row) =>
    row.weekly_timesheet_id != null &&
    String(row.weekly_timesheet_id).trim() !== "";

  const isEmptyRowMissingRequiredFields = (row) => {
    if (hasWeeklyTimesheetId(row)) return false;
    if (getWeekTotalHours(row) !== 0) return false;
    return (
      !hasRequiredId(row.project_id) ||
      !hasRequiredId(row.activity_id) ||
      !hasRequiredId(row.milestone_activity_mapping_id)
    );
  };

  const isNewRowCompleteSelectionWithNoHours = (row) => {
    if (hasWeeklyTimesheetId(row)) return false;
    if (getWeekTotalHours(row) !== 0) return false;
    return (
      hasRequiredId(row.project_id) &&
      hasRequiredId(row.activity_id) &&
      hasRequiredId(row.milestone_activity_mapping_id)
    );
  };

  const normalizeTimesheetData = (timesheetEntries, weeklyTimesheetDetails) => {
    const dayNames = [
      "saturday",
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
    ];

    const normalizedEntries = JSON.parse(JSON.stringify(timesheetEntries));
    const normalizedWeekly = JSON.parse(JSON.stringify(weeklyTimesheetDetails));

    normalizedEntries.forEach((entry, entryIndex) => {
      if (!entry.resource_timesheets) return;
      const dayToTimesheetId = {};
      entry.resource_timesheets.forEach((day) => {
        if (!day) return;

        try {
          const date = new Date(day.logged_date);
          if (isNaN(date.getTime())) return;

          const dayName = date
            .toLocaleDateString("en-US", { weekday: "long" })
            .toLowerCase();

          if (dayNames.includes(dayName) && day.timesheet_id) {
            dayToTimesheetId[dayName] = day.timesheet_id;
          }
        } catch (err) {
          console.error("Error processing date in normalization:", err);
        }
      });
      if (
        normalizedWeekly[entryIndex] &&
        normalizedWeekly[entryIndex].resource_timesheets
      ) {
        normalizedWeekly[entryIndex].resource_timesheets.forEach(
          (timesheet) => {
            if (!timesheet) return;

            try {
              const date = new Date(timesheet.logged_date);
              if (isNaN(date.getTime())) return;

              const dayName = date
                .toLocaleDateString("en-US", { weekday: "long" })
                .toLowerCase();

              if (dayNames.includes(dayName) && dayToTimesheetId[dayName]) {
                timesheet.timesheet_id = dayToTimesheetId[dayName];
              }
            } catch (err) {
              console.error("Error processing date in normalization:", err);
            }
          }
        );
      }
    });

    return {
      timesheetEntries: normalizedEntries,
      weeklyTimesheetDetails: normalizedWeekly,
    };
  };

  const handleSubmit = async (isSubmitted = true) => {
    try {
      setLoading(true);
      showLoading();

      if (!selectedFriday) {
        throw new Error("Please select a week to submit timesheet");
      }

      const filteredRows = timesheetEntries.filter((row) => {
        if (isEmptyRowMissingRequiredFields(row)) {
          return false;
        }

        if (isNewRowCompleteSelectionWithNoHours(row)) {
          return false;
        }

        const newId = row.id.split("temp-")[1];
        let isUUID = true;
        if (newId) {
          isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
            newId
          );
        }
        else
          isUUID =
            /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
              row.id
            );
        const totalHours = getWeekTotalHours(row);

        return !isUUID || totalHours > 0 || hasWeeklyTimesheetId(row);
      });

      const rowsToSubmit = filteredRows.map((row) => {
        const submissionRow = { ...row };
        if (!submissionRow.weekly_timesheet_id) {
          submissionRow.timesheet_id = null;
        }
        return submissionRow;
      });

      if (rowsToSubmit.length === 0) {
        throw new Error("Please add at least one timesheet entry");
      }

      const weeklyTimesheetDetails = rowsToSubmit.map((entry) => {
        if (!entry.project_id || !entry.activity_id) {
          throw new Error("Project and Activity are required for all entries");
        }

        const formattedWeek = selectedFriday.weekNumber;
        const fridayDate = new Date(selectedFriday.value);
        const weekDays = [];
        for (let i = 6; i >= 0; i--) {
          const date = new Date(fridayDate);
          date.setDate(fridayDate.getDate() - i);
          weekDays.push({
            date: date,
            dayName: date
              .toLocaleDateString("en-US", { weekday: "long" })
              .toLowerCase(),
          });
        }

        const resourceTimesheets = weekDays
          .map((dayInfo, index) => {
            const dayName = dayInfo.dayName;
            const hours = Number(entry[dayName] || 0);

            if (
              hours > 0 ||
              entry.resource_timesheets?.[index]?.timesheet_id !== null
            ) {
              return {
                timesheet_id:
                  entry.resource_timesheets?.[index]?.timesheet_id || null,
                timesheet_week: formattedWeek,
                logged_date: dayInfo.date.toISOString().split("T")[0],
                logged_hours: hours.toString(),
                milestone_activity_mapping_id: entry.milestone_activity_mapping_id ? parseInt(entry.milestone_activity_mapping_id) : null,
              };
            }
            return null;
          })
          .filter(Boolean);

        return {
          weekly_timesheet_id: entry.weekly_timesheet_id || null,
          task_id: parseInt(entry.activity_id),
          logged_by: parseInt(user.userDetails.resource_id),
          timesheet_week: formattedWeek,
          milestone_activity_mapping_id: entry.milestone_activity_mapping_id ? parseInt(entry.milestone_activity_mapping_id) : null,
          resource_comments: [
            ...(entry.resource_comments
              ?.filter(comment => !comment.__typename && !comment.id)
              .map(comment => ({
                project_manager_comment: comment.comment
              })) || [])
          ].filter(comment => comment.project_manager_comment),
          is_submitted: isSubmitted,
          resource_timesheets: resourceTimesheets,
        };
      });

      const {
        timesheetEntries: normalizedEntries,
        weeklyTimesheetDetails: normalizedWeekly,
      } = normalizeTimesheetData(rowsToSubmit, weeklyTimesheetDetails);

      const { success, error, data } = await addWeeklyTimesheet(normalizedWeekly);

      if (success) {
        const hasAnyDraft = data.some((entry) => entry.is_draft);
        setIsDraft(hasAnyDraft);
        setLastSavedDate(data[0]?.updated_on || null);

        const transformTimesheetData = (entry) => ({
          ...entry,
          project_id: entry.project_id.toString(),
          project_manager_comments: (entry.project_manager_comments || []).map(
            (comment) => ({
              ...comment,
              created_on: comment.created_on,
            })
          ),
          resource_comments: (entry.resource_comments || []).map((comment) => ({
            ...comment,
            created_on: comment.created_on,
          })),
        });

        const transformedEntries = data?.length
          ? data.map((entry) => ({
            id: uuidv4(),
            project_id: entry.project_id.toString(),
            activity_id: entry.task_id.toString(),
            actuals_budget: `${entry?.resource_actual_hours ? entry?.resource_actual_hours : 0
              }/${entry.resource_budget_hours}`,
            comments: entry.resource_comment || "",
            ...transformTimesheetData(entry),
            total_hours: entry.total_hours_for_week || "0",
            weekly_timesheet_id: entry.weekly_timesheet_id || null,
            milestone_activity_mapping_id: entry.milestone_activity_mapping_id ? entry.milestone_activity_mapping_id.toString() : "",
            resource_timesheets: entry.resource_timesheets,
            is_draft: entry.is_draft,
            saturday: "0",
            sunday: "0",
            monday: "0",
            tuesday: "0",
            wednesday: "0",
            thursday: "0",
            friday: "0",
            ...entry.resource_timesheets.reduce((acc, day) => {
              try {
                const date = new Date(day.logged_date);
                if (isNaN(date.getTime())) return acc;

                const dayName = date
                  .toLocaleDateString("en-US", { weekday: "long" })
                  .toLowerCase();

                if (
                  [
                    "saturday",
                    "sunday",
                    "monday",
                    "tuesday",
                    "wednesday",
                    "thursday",
                    "friday",
                  ].includes(dayName)
                ) {
                  acc[dayName] = day.logged_hours;
                }
                return acc;
              } catch (err) {
                console.error("Error processing date:", err);
                return acc;
              }
            }, {}),
          }))
          : DUMMY_TIMESHEET_ENTRIES;

        setTimesheetEntries(transformedEntries);
      } else {
        throw new Error(error || "Failed to submit timesheet");
      }

      setSuccessModal({
        type: "success",
        title: "Success",
        message: isSubmitted
          ? "Timesheet submitted successfully"
          : "Timesheet saved successfully",
      });
    } catch (err) {
      console.error("Error submitting timesheet:", err);
      setError(err.message);
      setSuccessModal({
        type: "error",
        title: "Action Failed",
        message: err.message || "An error occurred while submitting timesheet",
      });
    } finally {
      setLoading(false);
      hideLoading();
    }
  };

  const handleSave = async () => {
    await handleSubmit(false);
  };

  return {
    timesheetEntries,
    projects,
    loading,
    error,
    handleRowSave,
    addNewRow,
    handleDelete,
    handleSubmit,
    handleSave,
    fetchActivitiesForProject,
    fetchMilestoneActivityMappings,
    fridays,
    selectedFriday,
    handleFridayChange,
    handleDateChange,
    isDraft,
    handleProjectChange,
    successModal,
    setSuccessModal,
    lastSavedDate,
    holidays,
  };
};
